/*
  Menú THN — custom element <custom-menu>.
  Se usa custom element (y no IIFE + shopify:section:load) porque el editor de temas
  reemplaza el HTML del header al editar: connectedCallback/disconnectedCallback montan y
  limpian solos cada instancia nueva.

  Modo editor (Shopify.designMode): el panel abierto queda "fijado" (no se cierra por hover,
  foco ni al deseleccionar un bloque) y se restaura sin animación después de cada re-render
  de la sección, para poder editar con el panel a la vista.
*/
if (!customElements.get('custom-menu')) {
  /* ---------- Miniaturas automáticas (primer producto de una colección) ----------
     Se pide sections/custom-menu-thumb.liquid sobre la URL del enlace (Section Rendering API),
     así Shopify aplica los mismos filtros y orden que ve el cliente. Resultado cacheado en
     memoria y en localStorage para no repetir la petición en cada página. */
  const THUMB_SECTION = 'custom-menu-thumb';
  const THUMB_TTL = 12 * 60 * 60 * 1000; // 12 h: margen razonable para cambios de catálogo
  const REVEAL_TIMEOUT = 1800; // ms desde que se abre un panel hasta mostrar sus bloques aunque falte alguna imagen
  const thumbMemory = new Map();

  const thumbKey = (href) => {
    const url = new URL(href, window.location.origin);
    url.searchParams.delete('section_id');
    return url.pathname + url.search;
  };

  const readThumbCache = (key) => {
    try {
      const raw = window.localStorage.getItem('cm-thumb:' + key);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      return Date.now() - entry.t < THUMB_TTL ? entry.d : null;
    } catch (error) {
      return null;
    }
  };

  const writeThumbCache = (key, data) => {
    try {
      window.localStorage.setItem('cm-thumb:' + key, JSON.stringify({ t: Date.now(), d: data }));
    } catch (error) {
      /* incógnito o almacenamiento lleno: solo queda la caché en memoria */
    }
  };

  const fetchThumb = (href) => {
    const key = thumbKey(href);
    if (thumbMemory.has(key)) return thumbMemory.get(key);

    const cached = readThumbCache(key);
    if (cached) {
      const done = Promise.resolve(cached);
      thumbMemory.set(key, done);
      return done;
    }

    /* Siempre contra el dominio actual (sirve igual en thn.pe, myshopify y el editor) */
    const url = new URL(key, window.location.origin);
    url.searchParams.set('section_id', THUMB_SECTION);
    const request = fetch(url.toString(), { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const match = html.match(/<script[^>]*data-cm-thumb[^>]*>([\s\S]*?)<\/script>/);
        const data = match ? JSON.parse(match[1].trim() || '{}') : {};
        writeThumbCache(key, data);
        return data;
      })
      .catch(() => ({}));
    thumbMemory.set(key, request);
    return request;
  };

  class CustomMenu extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('.cm-nav');
      if (!this.nav) return;

      this.burger = document.querySelector('[data-cm-burger][aria-controls="' + this.nav.id + '"]');
      this.backdrop = this.querySelector('.cm-backdrop');
      this.wrapper = this.closest('.header-wrapper') || this.parentElement;
      this.desktopMq = window.matchMedia('(min-width: 990px)');
      this.hoverMq = window.matchMedia('(hover: hover)');
      this.openDelay = Number(this.dataset.openDelay) || 0;
      this.closeDelay = 220;
      this.switchDelay = 40;
      this.current = null;
      this.timer = null;
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.sectionId = this.dataset.sectionId;
      this.pinned = false;

      this.onPointerOver = this.onPointerOver.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onBurger = this.onBurger.bind(this);
      this.onMqChange = this.onMqChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
      this.onTrack = this.onTrack.bind(this);

      this.nav.addEventListener('pointerover', this.onPointerOver);
      this.nav.addEventListener('pointerleave', this.onPointerLeave);
      this.nav.addEventListener('click', this.onClick);
      /* Después de onClick: así se sabe si el clic navegó o solo abrió un panel */
      this.nav.addEventListener('click', this.onTrack);
      this.nav.addEventListener('auxclick', this.onTrack);
      this.nav.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('keydown', this.onKeydown);
      if (this.backdrop) this.backdrop.addEventListener('click', this.closeAll.bind(this));
      if (this.burger) {
        this.burger.addEventListener('click', this.onBurger);
        this.burger.addEventListener('keydown', this.onKeydown);
      }
      this.desktopMq.addEventListener('change', this.onMqChange);
      this.watchImages();

      if (this.designMode) {
        this.keepStylesheet();
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        this.restoreEditorState();
      }
      this.resumeEarly();
    }

    /* ---------- Carga ----------
       El menú se usa apenas carga el texto: las imágenes no bloquean nada. Se cargan por bloque
       (cada columna o banner): cuando están todas las de un bloque se muestran juntas, de arriba
       hacia abajo (.is-imgs-ready, animación en custom-menu.css). Mientras tanto se ve el texto
       y el fondo de las miniaturas. Si una imagen tarda, a los REVEAL_TIMEOUT ms de abrir el
       panel el bloque se muestra igual y la rezagada aparece sola al llegar.
       load/error no burbujean, por eso se escuchan en fase de captura. */
    watchImages() {
      const mark = (img, ok) => img.classList.add(ok ? 'is-loaded' : 'is-error');
      this.onImage = (event) => {
        if (event.target.localName !== 'img') return;
        mark(event.target, event.type === 'load');
        this.checkBlock(event.target.closest('.cm-col, .cm-promo'));
      };
      this.nav.addEventListener('load', this.onImage, true);
      this.nav.addEventListener('error', this.onImage, true);
      /* Las que ya llegaron (caché o panel abierto por CSS antes de cargar este script) */
      this.nav.querySelectorAll('.cm-panel img').forEach((img) => {
        if (img.complete) mark(img, img.naturalWidth > 0);
      });
      this.nav.querySelectorAll('.cm-col, .cm-promo').forEach((block) => this.checkBlock(block));
      this.classList.add('cm-ready');
    }

    /* Imágenes que el bloque realmente muestra (las ocultas por CSS no cuentan) */
    blockImages(block) {
      return [...block.querySelectorAll('img')].filter(
        (img) => !img.closest('.cm-col-text .cm-thumb, .cm-col-brands .cm-thumb, .cm-col-logos .cm-thumb-auto')
      );
    }

    checkBlock(block) {
      if (!block || block.classList.contains('is-imgs-ready')) return;
      /* Miniatura automática todavía sin pedir o sin respuesta */
      if (block.classList.contains('cm-col-image') && [...block.querySelectorAll('.cm-thumb-auto')].some((t) => !t.querySelector('img'))) return;
      const images = this.blockImages(block);
      if (images.every((img) => img.classList.contains('is-loaded') || img.classList.contains('is-error'))) {
        this.revealBlock(block);
      }
    }

    /* --cm-n ordena la entrada escalonada (el logo del título primero, después cada enlace).
       Solo las ya cargadas: una rezagada entra sin demora cuando llega. */
    revealBlock(block) {
      if (block.classList.contains('is-imgs-ready')) return;
      this.blockImages(block)
        .filter((img) => img.classList.contains('is-loaded'))
        .forEach((img, index) => img.style.setProperty('--cm-n', index));
      block.classList.add('is-imgs-ready');
    }

    revealSoon(item) {
      if (item.dataset.cmReveal) return;
      item.dataset.cmReveal = '1';
      setTimeout(() => {
        item.querySelectorAll('.cm-col, .cm-promo').forEach((block) => this.revealBlock(block));
      }, REVEAL_TIMEOUT);
    }

    /* Retoma lo que el cliente hizo mientras este script se descargaba:
       - Mouse ya sobre un ítem: pointerover no se repite hasta que el mouse cruce a otro
         elemento, así que el panel no se abría nunca. Se abre aquí, sin animar (el CSS ya lo
         estaba mostrando con :hover, ver custom-menu.css → "Antes de que cargue el JS").
       - Toques en la hamburguesa o en las flechas: se reproducen en orden (sections/header.liquid). */
    resumeEarly() {
      if (!this.designMode && this.isDesktop && this.hoverMq.matches) {
        const hovered = this.nav.querySelector('.cm-item.has-panel:hover');
        if (hovered) {
          this.setInstant(true);
          this.openPanel(hovered, { instant: true });
        }
      }

      const early = window.cmEarly;
      if (!early) return;
      document.removeEventListener('click', early.handler, true);
      window.cmEarly = null;
      early.queue.forEach((target) => {
        if (!target.isConnected) return;
        if (target === this.burger) {
          this.onBurger();
        } else if (this.nav.contains(target)) {
          this.onClick({ target });
        }
      });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.burger) {
        this.burger.removeEventListener('click', this.onBurger);
        this.burger.removeEventListener('keydown', this.onKeydown);
      }
      if (this.desktopMq) this.desktopMq.removeEventListener('change', this.onMqChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      if (this.nav && this.onImage) {
        this.nav.removeEventListener('load', this.onImage, true);
        this.nav.removeEventListener('error', this.onImage, true);
      }
      this.lock(false);
    }

    get isDesktop() {
      return this.desktopMq.matches;
    }

    topItemFrom(target) {
      const item = target.closest('.cm-item');
      return item && this.nav.contains(item) ? item : null;
    }

    schedule(fn, delay) {
      clearTimeout(this.timer);
      this.timer = delay > 0 ? setTimeout(fn, delay) : (fn(), null);
    }

    /* Precarga las imágenes lazy del panel mientras corre el delay de apertura. Se saltan las
       que el CSS oculta (miniaturas en columnas "Solo texto"/"Marcas", banners solo de
       escritorio en móvil): con loading="eager" se descargarían igual, sin mostrarse, y le
       quitarían ancho de banda a las que sí se ven. */
    warm(item) {
      if (item.dataset.cmWarm) return;
      item.dataset.cmWarm = '1';
      const hidden = this.isDesktop ? '.cm-show-mobile' : '.cm-show-desktop';
      item.querySelectorAll('.cm-panel img[loading="lazy"]').forEach((img) => {
        if (img.closest('.cm-col-text .cm-thumb, .cm-col-brands .cm-thumb, ' + hidden)) return;
        img.loading = 'eager';
      });
      this.autoThumbs(item);
    }

    /* Completa las miniaturas sin imagen manual (solo columnas "Con imagen") */
    autoThumbs(item) {
      item.querySelectorAll('.cm-col-image .cm-thumb-auto:not([data-cm-done])').forEach((thumb) => {
        thumb.dataset.cmDone = '1';
        fetchThumb(thumb.dataset.cmAuto).then((data) => {
          if (!data || !data.src) {
            const block = thumb.closest('.cm-col');
            thumb.remove();
            this.checkBlock(block);
            return;
          }
          const img = document.createElement('img');
          img.src = data.src;
          img.srcset = data.srcset || '';
          img.sizes = thumb.closest('.cm-panel-inner.is-single')
            ? '(min-width: 990px) 320px, 146px'
            : '(min-width: 990px) 100px, 146px';
          img.alt = data.alt || '';
          img.width = 320;
          img.height = 320;
          img.decoding = 'async';
          img.addEventListener('load', () => thumb.classList.add('is-loaded'), { once: true });
          thumb.appendChild(img);
        });
      });
    }

    /* Bloqueo de scroll del drawer. Solo se bloquea el body (su overflow pasa al viewport):
       si también se bloqueara <html>, el body se volvería el contenedor de scroll del header
       sticky y este saltaría a su posición original, fuera de pantalla. Mientras el drawer
       está abierto el header no se oculta (menu-open lo mantiene en top: 0, preventHide
       evita que el scroll inercial de iOS lo esconda). */
    lock(on) {
      document.documentElement.classList.toggle('cm-locked', on);
      const header = this.wrapper && this.wrapper.closest('.section-header');
      if (header) header.classList.toggle('menu-open', on);
      if (this.wrapper && this.wrapper.localName === 'sticky-header') this.wrapper.preventHide = on;
    }

    updateTop() {
      const bottom = Math.max(0, Math.round(this.wrapper.getBoundingClientRect().bottom));
      this.style.setProperty('--cm-top', bottom + 'px');
    }

    setItem(item, open) {
      item.classList.toggle('is-open', open);
      const toggle = item.querySelector('.cm-top-toggle');
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    /* Apertura sin animaciones (restaurar tras un re-render del editor). Se quita la clase
       de animación de columnas y se devuelve recién cuando ese panel se cierra; si se
       devolviera con el panel abierto, la cascada se volvería a reproducir. */
    setInstant(on) {
      if (on) {
        this.animClass = this.animClass || [...this.classList].find((c) => c.startsWith('cm-anim-'));
        if (this.animClass) this.classList.remove(this.animClass);
        this.classList.add('cm-instant');
      } else if (this.classList.contains('cm-instant')) {
        this.classList.remove('cm-instant');
        if (this.animClass) this.classList.add(this.animClass);
      }
    }

    /* ---------- Escritorio ---------- */
    openPanel(item, options = {}) {
      clearTimeout(this.timer);
      if (options.pin) this.pinned = true;
      if (this.current === item) return this.saveEditorState();
      const switching = Boolean(this.current);
      if (this.current) this.setItem(this.current, false);
      if (!options.instant) this.setInstant(false);
      this.nav.classList.toggle('is-switching', switching);
      this.warm(item);
      this.revealSoon(item);
      this.updateTop();
      this.setItem(item, true);
      this.current = item;
      this.classList.add('is-active');
      this.saveEditorState();
    }

    closePanel() {
      clearTimeout(this.timer);
      this.pinned = false;
      if (this.current) {
        this.setItem(this.current, false);
        this.current = null;
      }
      this.setInstant(false);
      this.nav.classList.remove('is-switching');
      this.classList.remove('is-active');
      this.saveEditorState();
    }

    onPointerOver(event) {
      if (!this.isDesktop || event.pointerType !== 'mouse' || this.pinned) return;
      const item = this.topItemFrom(event.target);
      if (!item || item === this.current) {
        clearTimeout(this.timer);
        return;
      }
      if (item.classList.contains('has-panel')) {
        this.warm(item);
        this.schedule(() => this.openPanel(item), this.current ? this.switchDelay : this.openDelay);
      } else if (this.current) {
        this.schedule(() => this.closePanel(), this.switchDelay);
      } else {
        clearTimeout(this.timer);
      }
    }

    onPointerLeave(event) {
      if (!this.isDesktop || event.pointerType !== 'mouse' || this.pinned) return;
      this.schedule(() => this.closePanel(), this.closeDelay);
    }

    onFocusOut(event) {
      if (!this.isDesktop || !this.current || this.pinned) return;
      if (event.relatedTarget && this.nav.contains(event.relatedTarget)) return;
      if (!event.relatedTarget && this.nav.matches(':hover')) return;
      this.closePanel();
    }

    /* ---------- Móvil ---------- */
    openDrawer(options = {}) {
      this.updateTop();
      if (options.instant) this.setInstant(true);
      this.classList.add('is-drawer-open');
      this.lock(true);
      if (this.burger) this.burger.setAttribute('aria-expanded', 'true');
      if (!options.instant) {
        const first = this.nav.querySelector('.cm-top-toggle, .cm-top-link');
        if (first) first.focus({ preventScroll: true });
      }
      this.saveEditorState();
    }

    closeDrawer(returnFocus) {
      if (!this.classList.contains('is-drawer-open')) return;
      this.closeSub(false);
      this.classList.remove('is-drawer-open');
      this.setInstant(false);
      this.lock(false);
      if (this.burger) {
        this.burger.setAttribute('aria-expanded', 'false');
        if (returnFocus) this.burger.focus();
      }
      this.saveEditorState();
    }

    openSub(item, options = {}) {
      if (this.current && this.current !== item) {
        this.setItem(this.current, false);
        if (!options.instant) this.setInstant(false);
      }
      this.warm(item);
      this.revealSoon(item);
      this.setItem(item, true);
      this.current = item;
      this.nav.classList.add('has-sub');
      if (!options.instant) {
        const back = item.querySelector('.cm-back');
        if (back) back.focus({ preventScroll: true });
      }
      this.saveEditorState();
    }

    closeSub(returnFocus) {
      if (!this.current) return;
      const item = this.current;
      this.setItem(item, false);
      this.current = null;
      this.nav.classList.remove('has-sub');
      if (returnFocus) {
        const toggle = item.querySelector('.cm-top-toggle');
        if (toggle) toggle.focus({ preventScroll: true });
      }
      this.saveEditorState();
    }

    onBurger() {
      if (this.classList.contains('is-drawer-open')) {
        this.closeDrawer(false);
      } else {
        this.openDrawer();
      }
    }

    /* ---------- Compartido ---------- */
    onClick(event) {
      if (event.target.closest('.cm-editor-close')) {
        this.closeAll();
        return;
      }

      const toggle = event.target.closest('.cm-top-toggle');
      if (toggle) {
        const item = this.topItemFrom(toggle);
        if (this.isDesktop) {
          item === this.current ? this.closePanel() : this.openPanel(item, { pin: this.designMode });
        } else {
          this.openSub(item);
        }
        return;
      }

      if (event.target.closest('.cm-back')) {
        this.closeSub(true);
        return;
      }

      /* Primer clic/toque sobre un título con panel lo abre en vez de navegar:
         pantallas táctiles grandes (tablet horizontal) y editor de temas (queda fijado) */
      const link = event.target.closest('.cm-top-link');
      if (link && this.isDesktop && (this.designMode || !this.hoverMq.matches)) {
        const item = this.topItemFrom(link);
        if (item && item.classList.contains('has-panel') && (item !== this.current || (this.designMode && !this.pinned))) {
          event.preventDefault();
          this.openPanel(item, { pin: this.designMode });
        }
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.isDesktop) {
        if (!this.current) return;
        const toggle = this.current.querySelector('.cm-top-toggle');
        this.closePanel();
        if (toggle) toggle.focus();
      } else if (this.current) {
        this.closeSub(true);
      } else {
        this.closeDrawer(true);
      }
    }

    /* ---------- Medición (Google Tag Manager) ----------
       Cada clic que navega desde el menú envía al dataLayer el evento "menu_click" con el
       nivel, el texto, su padre y la ruta completa (ej.: Hombre > Zapatillas > Nike Dunk).
       No se envía en el editor ni cuando el clic solo abre un panel. */
    onTrack(event) {
      if (this.designMode || event.defaultPrevented) return;
      if (event.type === 'auxclick' && event.button !== 1) return; // solo clic con rueda (nueva pestaña)
      const link = event.target.closest('a[href]');
      if (!link || !this.nav.contains(link)) return;
      const data = this.trackData(link);
      if (!data) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(data);
    }

    trackData(link) {
      /* GA4 acepta hasta 100 caracteres por parámetro */
      const clean = (text) => (text || '').replace(/\s+/g, ' ').trim().slice(0, 100) || undefined;
      const item = link.closest('.cm-item');
      const col = link.closest('.cm-col');
      const promo = link.closest('.cm-promo');
      const level1 = item ? clean(item.dataset.cmLabel) : undefined;
      let level2;
      let level;
      let type;
      let text;

      if (link.classList.contains('cm-top-link') || link.classList.contains('cm-view-all')) {
        level = 1;
        type = link.classList.contains('cm-view-all') ? 'view_all' : 'item';
        text = level1;
      } else if (promo) {
        level = 2;
        type = 'banner';
        text = level2 = clean(promo.dataset.cmLabel);
      } else if (col) {
        level2 = clean(col.dataset.cmLabel);
        if (link.classList.contains('cm-col-title')) {
          level = 2;
          type = 'column';
          text = level2;
        } else if (link.classList.contains('cm-badge')) {
          /* La etiqueta (NEW) pertenece a la columna: se registra como variante de nivel 2 */
          level = 2;
          type = 'badge';
          text = clean((level2 ? level2 + ' ' : '') + '[' + link.textContent.trim() + ']');
        } else if (link.classList.contains('cm-link')) {
          const label = link.querySelector('.cm-link-label');
          level = 3;
          type = 'link';
          text = clean(label ? label.textContent : link.textContent);
        }
      }
      if (!level) return null;

      const chain = level === 1 ? [text] : level === 2 ? [level1, text] : [level1, level2, text];
      const url = new URL(link.href, window.location.origin);
      return {
        event: 'menu_click',
        menu_level: level,
        menu_type: type,
        menu_text: text,
        menu_parent: level === 1 ? undefined : level === 2 ? level1 : level2 || level1,
        menu_level1: level1,
        menu_level2: level2,
        menu_path: clean(chain.filter(Boolean).join(' > ')),
        menu_url: clean(url.origin === window.location.origin ? url.pathname + url.search : url.href),
        menu_device: this.isDesktop ? 'desktop' : 'mobile'
      };
    }

    closeAll() {
      if (this.isDesktop) {
        this.closePanel();
      } else {
        this.closeDrawer(false);
      }
    }

    onMqChange() {
      clearTimeout(this.timer);
      if (this.current) this.setItem(this.current, false);
      this.current = null;
      this.pinned = false;
      this.setInstant(false);
      this.nav.classList.remove('is-switching', 'has-sub');
      this.classList.remove('is-active', 'is-drawer-open');
      this.lock(false);
      if (this.burger) this.burger.setAttribute('aria-expanded', 'false');
      this.saveEditorState();
    }

    /* ---------- Editor de temas ---------- */

    /* El <link> de custom-menu.css vive dentro del HTML del header: en cada re-render del
       editor se quita y se vuelve a insertar, y durante unos milisegundos el menú queda sin
       estilos (parpadeo). Se deja una copia fija en <head> que sobrevive a los re-renders. */
    keepStylesheet() {
      if (document.head.querySelector('link[data-cm-editor-css]')) return;
      const link = document.querySelector('link[rel="stylesheet"][href*="custom-menu.css"]');
      if (!link) return;
      const copy = link.cloneNode();
      copy.setAttribute('data-cm-editor-css', '');
      document.head.appendChild(copy);
    }

    editorStore() {
      window.__cmEditorState = window.__cmEditorState || {};
      return window.__cmEditorState;
    }

    /* Guarda qué panel está abierto; la variable vive en la ventana del editor y sobrevive
       a los re-renders de la sección (el elemento se destruye y se vuelve a crear) */
    saveEditorState() {
      if (!this.designMode) return;
      this.editorStore()[this.nav.id] = {
        item: this.current ? this.current.dataset.cmBlock : null,
        drawer: this.classList.contains('is-drawer-open')
      };
    }

    restoreEditorState() {
      const state = this.editorStore()[this.nav.id];
      if (!state) return;
      const item = state.item ? this.nav.querySelector('.cm-item[data-cm-block="' + state.item + '"]') : null;
      const hasPanel = item && item.classList.contains('has-panel');
      if (this.isDesktop) {
        if (!hasPanel) return;
        this.setInstant(true);
        this.openPanel(item, { pin: true, instant: true });
      } else if (state.drawer) {
        this.openDrawer({ instant: true });
        if (hasPanel) this.openSub(item, { instant: true });
      }
    }

    /* Al seleccionar un bloque del menú en la barra lateral se abre (y fija) su panel */
    onBlockSelect(event) {
      const detail = event.detail || {};
      if (this.sectionId && detail.sectionId && detail.sectionId !== this.sectionId) return;
      const el = detail.blockId ? this.nav.querySelector('[data-cm-block="' + detail.blockId + '"]') : null;
      if (!el) return;
      const item = this.topItemFrom(el);
      if (!item) return;
      if (!item.classList.contains('has-panel')) {
        this.closeAll();
        return;
      }
      if (this.isDesktop) {
        this.openPanel(item, { pin: true });
      } else {
        if (!this.classList.contains('is-drawer-open')) this.openDrawer();
        this.openSub(item);
      }
    }

    /* El panel solo se cierra al salir del header en el editor (o con Esc / "Cerrar panel") */
    onSectionDeselect(event) {
      const detail = event.detail || {};
      if (this.sectionId && detail.sectionId !== this.sectionId) return;
      this.closeAll();
    }
  }

  customElements.define('custom-menu', CustomMenu);
}
